import { useEffect, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  AccountErrorKind,
  AnalyzeResult,
  LayoutMapping,
  Settings,
  SettingsAccount,
} from '../../lib/settings-types.js';
import { api } from '../../lib/api.js';
import { companyName } from '../../lib/ignored-types.js';
import { SourceEditDialog } from './SourceEditDialog.js';
import { DuplicateEmailSources } from './DuplicateEmailSources.js';
import { MappingEditor, mappingProblems } from '../../components/MappingEditor.js';
import {
  canLearnLayout,
  canSaveLayout,
  layoutAnalyzePath,
  layoutLine,
  layoutStorePath,
  learnErrorText,
  learnLayoutCopy,
  needsLearnLayout,
  NO_LAYOUT_RE,
} from '../../lib/layout-view.js';
import {
  Button,
  Card,
  Field,
  StatusPill,
  TextInput,
  Toggle,
  Toolbar,
  useConfirm,
  type Tone,
} from '../../components/ui/index.js';
import { errorMessage, fmtWhen, useIsAdmin, useSettingsPost } from './common.js';

type Confirm = ReturnType<typeof useConfirm>['confirm'];

interface AccountPatch {
  accountId: string;
  fetchHour?: number | null;
  label?: string;
  ownerUserId?: number | null;
  enabled?: boolean;
  currencyMode?: string;
  fxBasis?: string;
  pastRows?: 'convert' | 'keep';
}

/**
 * What a failing source needs DONE about it, in the words of someone who has to
 * do it. The stored message is written for whoever wrote the code — "swaarm
 * returned HTTP 401 with an empty body" is precise and tells an operator
 * nothing about whether to fix a token, re-map a column, or simply wait. It
 * stays on screen underneath (it is what you quote to the provider); this goes
 * above it. Every repair named here is IN PLACE, on this card: re-adding a
 * source through Add stats mints a second account for the same feeds and
 * counts their revenue twice (R8).
 */
const ERROR_HELP: Record<AccountErrorKind, { headline: string; advice: string }> = {
  credentials: {
    headline: 'The provider rejected our credentials.',
    advice:
      'Nothing is wrong with the numbers or the layout — the token, key, or sheet access is no longer accepted. Check it in the provider’s own dashboard, then put the working one on this source: Replace credentials (by name) or Edit → Source URL. Stored history is untouched meanwhile.',
  },
  layout: {
    headline: 'The source changed shape.',
    advice:
      'A column this source is mapped to is gone or renamed, so the import was refused rather than filed into the wrong place. Use Learn layout on this card to re-map the columns; the old mapping was not applied.',
  },
  network: {
    headline: 'We could not reach the source.',
    advice:
      'Nothing was imported and nothing was lost. This is usually temporary; if it persists, check that the URL is still right and that the host answers from the server.',
  },
};

/** Freshness in one word. This was four separate columns of prose before. */
function health(a: SettingsAccount): { tone: Tone; text: string } {
  if (!a.enabled) return { tone: 'neutral', text: 'Paused' };
  if (a.lastErrorKind === 'credentials') return { tone: 'error', text: 'Credentials' };
  if (a.lastError) return { tone: 'error', text: 'Failing' };
  const last = a.lastSuccessAt ? Date.parse(a.lastSuccessAt) : NaN;
  if (Number.isNaN(last)) return { tone: 'warn', text: 'Never pulled' };
  if (Date.now() - last > 26 * 3600_000) return { tone: 'warn', text: 'Stale' };
  return { tone: 'ok', text: 'Live' };
}

/**
 * Providers: one card per source.
 *
 * It was an eight-column table that mixed four display columns with six live
 * editors and ran off the right edge of the pane. A card holds the same
 * controls at a readable width, puts each one behind a label, and gives every
 * change a visible result — the selects used to save on change and say nothing
 * at all, and a rejected change was reverted by the next refetch with no
 * message.
 */
export function SourcesPane({ settings }: { settings: Settings }) {
  const { confirm, dialog } = useConfirm();
  const unignore = useSettingsPost<{ host: string }>('/api/settings/template-unignore');

  const ignored = settings.templates.filter((t) => t.system === 'ignored');
  const learned = settings.templates.filter((t) => t.system !== 'ignored');

  return (
    <div>
      <p className="table-note">
        Every stats source and how fresh it is. <b>Edit</b> opens everything about one — its name,
        who sees it, when it crawls, and the URL it pulls from, token included. Replace credentials
        puts a new token on a source by name, and Learn layout re-reads a generic source&apos;s
        columns — all on the source that already exists, so its history and links stay attached
        and no second source is made. Add stats is for sources you do not have yet.
      </p>

      {(settings.undecryptableAccounts ?? []).length ? (
        <div className="cfg-callout src-error" role="status">
          <b>TRAFFIC_SECRET_KEY changed</b> — the stored credentials of{' '}
          {(settings.undecryptableAccounts ?? []).join(', ')} can no longer be decrypted. Put each one back with
          Replace credentials (or Edit → Source URL); until then those sources pull nothing.
        </div>
      ) : null}

      {(settings.duplicateCredentials ?? []).map((g) => (
        <div key={g.fingerprint} className="cfg-callout src-error" role="status">
          <b>These sources share one key</b> —{' '}
          {g.accounts.map((a) => `${a.id} (${a.rep}${a.label ? `, ${a.label}` : ''}${a.enabled ? '' : ', paused'})`).join(' and ')} pull the same{' '}
          {g.provider} feeds and count the revenue twice. Pause or delete one of them; a new source with a key already in use is refused.
        </div>
      ))}

      <DuplicateEmailSources />

      {settings.accounts.map((a) => (
        <SourceCard key={a.id} account={a} settings={settings} confirm={confirm} />
      ))}

      <Card
        title="Learned layouts"
        description="The column mapping learned the first time each source/report was seen. Every later pull is checked against it — a renamed column stops the import instead of mis-filing numbers."
      >
        <div className="table-scroll">
          <table className="stat-table">
            <thead>
              <tr>
                <th className="l">Key</th>
                <th className="l">Kind</th>
                <th>Learned</th>
              </tr>
            </thead>
            <tbody>
              {learned.map((t) => (
                <tr key={t.host}>
                  <td className="l mono">{t.host}</td>
                  <td className="l">{t.system}</td>
                  <td>{fmtWhen(t.createdAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {ignored.length ? (
        <Card
          title="Ignored email report types"
          description="Dismissed as not-a-stats-report. Un-ignore to reconsider on the next scan."
        >
          {ignored.map((t) => (
            <div key={t.host} className="ignored-row">
              <span className="ignored-row__who">
                <b>{companyName(t.sender, t.host)}</b>
                {t.filename ? <> · {t.filename}</> : null}
                {t.sender ? <span className="muted"> · from {t.sender}</span> : null}
              </span>
              <span className="mono muted">{t.host}</span>
              <Button variant="ghost" size="sm" onClick={() => unignore.mutate({ host: t.host })}>
                Un-ignore
              </Button>
            </div>
          ))}
        </Card>
      ) : null}

      {dialog}
    </div>
  );
}

function SourceCard({
  account: a,
  settings,
  confirm,
}: {
  account: SettingsAccount;
  settings: Settings;
  confirm: Confirm;
}) {
  const update = useSettingsPost<AccountPatch>('/api/settings/account');
  const patch = (body: Omit<AccountPatch, 'accountId'>) =>
    update.mutateAsync({ accountId: a.id, ...body });

  const [editing, setEditing] = useState(false);
  // Replace credentials writes a shared secret (server: admin). Only shown
  // for providers that hold one — upload/email sources carry a filename or
  // a sender there, not a token.
  const isAdmin = useIsAdmin();
  const [replacing, setReplacing] = useState(false);
  const canReplace = isAdmin && Object.hasOwn(CREDENTIAL_FIELDS, a.provider);

  // Learn layout (P2g): the Add-stats learn-and-confirm flow on THIS source.
  // Opened from the callout that replaces the "no stored layout" error, or
  // from the card's actions for any generic source (re-learn after a
  // provider changes its columns). Admin: the routes write a shared template.
  const canLearn = canLearnLayout(a, isAdmin);
  // One Learn layout control at a time (2h item 4): the callout carries it
  // while the source has no layout; the header action otherwise.
  const needsLayout = needsLearnLayout(a);
  const [learning, setLearning] = useState(false);
  const [runError, setRunError] = useState<string | null>(null);

  // Run now: enqueue a crawl for just this account (POST /api/refresh?account=…).
  // Returns as soon as it's queued; the worker does the pull. Status on the card
  // updates on the next settings refetch, so nudge one a few seconds later.
  const qc = useQueryClient();
  const [justRan, setJustRan] = useState(false);
  const markQueued = () => {
    setJustRan(true);
    setTimeout(() => setJustRan(false), 3000);
    setTimeout(() => void qc.invalidateQueries({ queryKey: ['settings'] }), 8000);
  };
  const runNow = useMutation({
    mutationFn: () => api.post(`/api/refresh?account=${encodeURIComponent(a.id)}`),
    onSuccess: () => {
      setRunError(null);
      markQueued();
    },
    onError: (err: unknown) => {
      // A generic source refused for want of a layout opens the learn step
      // instead of printing the error it would have printed.
      const msg = errorMessage(err);
      if (canLearn && NO_LAYOUT_RE.test(msg)) setLearning(true);
      else setRunError(msg);
    },
  });

  const state = health(a);
  const title = a.label ?? a.id;

  const toggleEnabled = async () => {
    if (a.enabled) {
      const ok = await confirm<boolean>({
        title: `Stop crawling ${title}?`,
        body: (
          <p>
            Its stored history stays exactly as it is; nothing new arrives until you switch it
            back on.
          </p>
        ),
        choices: [{ label: 'Pause crawling', value: true, variant: 'danger' }],
      });
      if (!ok) return;
    }
    await patch({ enabled: !a.enabled });
  };

  // What the editable fields are set to, read at a glance. They were four live
  // controls on the card before; the card is for seeing, the dialog for
  // changing, and a source in trouble is read far more often than edited.
  const owner = settings.users.find((u) => u.id === a.ownerUserId)?.username ?? 'nobody';
  const summary = [
    `Dashboard user: ${owner}`,
    `Crawl hour: ${a.fetchHour == null ? `global (${settings.defaultFetchHour}:00)` : `${a.fetchHour}:00`}`,
    `Currency: ${a.currencyMode === 'multi' ? 'Auto' : a.currencyMode}`,
    ...(a.provider === 'generic' && /docs\.google\.com/.test(a.editUrl ?? '') ? [a.followWorkbook ? 'follows the workbook' : 'reads this tab only'] : []),
  ].join(' · ');

  return (
    <Card
      className={a.enabled ? undefined : 'row-disabled'}
      status={<StatusPill tone={state.tone}>{state.text}</StatusPill>}
      title={title}
      description={
        <>
          {a.providerLabel ?? a.provider} · {a.rep} · {a.feeds} feed{a.feeds === 1 ? '' : 's'} ·
          last data {fmtWhen(a.lastData)}
          {a.inbox ? ` · via ${a.inbox}` : ''}
          <br />
          <code className="mono" title="This source's id">
            {a.id}
          </code>
          {a.statDays > 0
            ? ` · holds ${a.statDays} day${a.statDays === 1 ? '' : 's'}, ${a.firstDay} → ${a.lastDay}`
            : ' · holds no days yet'}
        </>
      }
      actions={
        <>
          <Button
            size="sm"
            onClick={() => runNow.mutate()}
            disabled={!a.enabled || runNow.isPending}
            title={a.enabled ? 'Crawl this source now' : 'Paused — switch it on to crawl'}
          >
            {runNow.isPending ? 'Queuing…' : justRan ? 'Queued ✓' : 'Run now'}
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setEditing(true)}>
            Edit
          </Button>
          {canReplace ? (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setReplacing((v) => !v)}
              aria-expanded={replacing}
              title="Put a new token on this source — no second source is made"
            >
              Replace credentials
            </Button>
          ) : null}
          {canLearn && !needsLayout ? (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setLearning((v) => !v)}
              aria-expanded={learning}
              title="Re-read the source and confirm its columns — the layout is stored on this source, no second source is made"
            >
              Learn layout
            </Button>
          ) : null}
          <Toggle
            checked={a.enabled}
            onChange={() => void toggleEnabled()}
            label={`Crawl ${title}`}
            busy={update.isPending}
          />
        </>
      }
    >
      {a.lastError ? (
        <SourceError account={a} canLearn={canLearn} learning={learning} onLearn={() => setLearning(true)} />
      ) : null}
      {runError ? <p className="form-err">{runError}</p> : null}
      {a.lastWarning ? <p className="form-warn">{a.lastWarning}</p> : null}

      <p className="cfg-summary">{summary}</p>
      {layoutLine(a.layout) ? <p className="cfg-summary cfg-layout">{layoutLine(a.layout)}</p> : null}

      {replacing && canReplace ? (
        <CredentialsForm account={a} onClose={() => setReplacing(false)} />
      ) : null}
      {learning && canLearn ? (
        <LayoutLearner
          account={a}
          onClose={() => setLearning(false)}
          onSaved={(queued) => {
            setRunError(null);
            if (queued) markQueued();
            else void qc.invalidateQueries({ queryKey: ['settings'] });
          }}
        />
      ) : null}

      {a.editUrl ? (
        <p className="cfg-url">
          Source URL <code>{a.editUrl}</code>
        </p>
      ) : null}

      {editing ? (
        <SourceEditDialog account={a} settings={settings} onClose={() => setEditing(false)} />
      ) : null}
    </Card>
  );
}

/**
 * A failure, explained then quoted. The explanation is keyed off the kind the
 * server classified (accounts/error-kind.ts), so the two never drift into
 * disagreeing about what went wrong; an unclassified error just shows the
 * message, exactly as it always did. A generic source with NO stored layout
 * is a state, not a fault: the callout carries the button that fixes it, here,
 * on this source (P2g), and the raw message stays beneath.
 */
function SourceError({
  account,
  canLearn,
  learning,
  onLearn,
}: {
  account: SettingsAccount;
  canLearn: boolean;
  learning: boolean;
  onLearn: () => void;
}) {
  const learnable = needsLearnLayout(account);
  const help = learnable
    ? {
        headline: 'This source has no stored layout yet.',
        advice: canLearn
          ? 'Its crawl could not read the columns. Learn it here — the columns are confirmed before anything is stored, and no second source is made.'
          : 'Its crawl could not read the columns. An admin can learn it from this card.',
      }
    : account.lastErrorKind
      ? ERROR_HELP[account.lastErrorKind]
      : null;
  return (
    <div className={learnable ? 'cfg-callout src-error' : 'form-err src-error'} role="status">
      {help ? (
        <p className="src-error-lead">
          <strong>{help.headline}</strong> {help.advice}
        </p>
      ) : null}
      {learnable && canLearn && !learning ? (
        <Button size="sm" onClick={onLearn}>
          Learn layout
        </Button>
      ) : null}
      <p className="src-error-raw">{account.lastError}</p>
    </div>
  );
}

/**
 * Learn layout for THIS source (P2g): the Add-stats review step, in place.
 * Opening it reads the source through the account's own URL and identities
 * (POST /api/accounts/:id/layout/analyze) and shows the proposed - or the
 * already-learned - column matching; Save stores the confirmed mapping under
 * the key the crawl reads and queues the same crawl Run now queues (POST
 * /api/accounts/:id/layout). No second source is made.
 */
function LayoutLearner({
  account: a,
  onClose,
  onSaved,
}: {
  account: SettingsAccount;
  onClose: () => void;
  onSaved: (queued: boolean) => void;
}) {
  const qc = useQueryClient();
  const [mapping, setMapping] = useState<LayoutMapping | null>(null);
  const [error, setError] = useState<string | null>(null);
  const analysis = useQuery({
    queryKey: ['layout-analyze', a.id],
    queryFn: () => api.post<AnalyzeResult & { templateKey?: string }>(layoutAnalyzePath(a.id)),
    retry: false,
    staleTime: Infinity,
    // Re-read on every open: the point of a re-learn is the source's columns NOW.
    gcTime: 0,
  });
  useEffect(() => {
    if (analysis.data) setMapping(analysis.data.mapping ? structuredClone(analysis.data.mapping) : null);
  }, [analysis.data]);
  const save = useMutation({
    mutationFn: () =>
      api.post<{ ok: boolean; learned: boolean; templateKey: string; queued: boolean }>(layoutStorePath(a.id), {
        mapping,
        // The header set the mapping was confirmed on - the snapshot every
        // later pull is checked against.
        ...((analysis.data?.snapshotColumns ?? analysis.data?.columns)
          ? { columns: analysis.data!.snapshotColumns ?? analysis.data!.columns }
          : {}),
      }),
    onSuccess: (res) => {
      void qc.invalidateQueries({ queryKey: ['settings'] });
      onSaved(res.queued);
      onClose();
    },
    onError: (err: unknown) => setError(learnErrorText(err)),
  });
  const problems = mapping ? mappingProblems(mapping) : [];
  const mode = analysis.data?.mode;

  return (
    <div className="cfg-callout" role="group" aria-label={`Learn layout for ${a.label ?? a.id}`}>
      {analysis.isPending ? <p className="table-note">Reading the source…</p> : null}
      {analysis.isError ? <p className="ui-field__error">{learnErrorText(analysis.error)}</p> : null}
      {analysis.data ? (
        <>
          <p className="table-note">
            <b>Review the columns</b> — {learnLayoutCopy(analysis.data.mode)} Confirm the columns
            before anything is stored.
            {analysis.data.templateKey ? (
              <>
                {' '}
                Stored as layout <code>{analysis.data.templateKey}</code>.
              </>
            ) : null}
          </p>
          {mode !== 'empty' && mapping ? (
            <MappingEditor
              columns={analysis.data.columns ?? []}
              samples={analysis.data.samples ?? {}}
              mapping={mapping}
              onChange={setMapping}
            />
          ) : null}
        </>
      ) : null}
      <Toolbar className="cfg-actions">
        <Button
          size="sm"
          onClick={() => save.mutate()}
          loading={save.isPending}
          disabled={!canSaveLayout(mode, mapping != null, problems.length)}
        >
          Save layout and run
        </Button>
        <Button variant="ghost" size="sm" onClick={onClose} disabled={save.isPending}>
          Cancel
        </Button>
        {error ? (
          <span className="ui-field__error" role="status">
            {error}
          </span>
        ) : null}
      </Toolbar>
    </div>
  );
}

/** What each crawlable provider's fetcher needs (server: ingest/source-url.ts
 * REQUIRED_CREDENTIAL_KEYS, plus the optional sheet tab). The form asks for
 * exactly these, by name, so nobody has to write JSON to fix a token. */
type CredKey = 'token' | 'pubid' | 'key' | 'sheetId' | 'gid' | 'url';
interface CredField {
  key: CredKey;
  label: string;
  secret: boolean;
  hint?: string;
  placeholder?: string;
}
const CREDENTIAL_FIELDS: Record<string, CredField[]> = {
  swaarm: [
    { key: 'token', label: 'Token', secret: true, hint: 'The token= value from the report URL.' },
  ],
  justforspends: [
    { key: 'token', label: 'Token', secret: true, hint: 'The token= value from the report URL.' },
  ],
  aemdays: [
    { key: 'pubid', label: 'Publisher ID', secret: false },
    { key: 'key', label: 'Key', secret: true },
  ],
  trafficguru: [
    {
      key: 'sheetId',
      label: 'Sheet ID',
      secret: false,
      hint: 'The long id between /d/ and /edit in the sheet link.',
    },
    { key: 'gid', label: 'Tab (gid)', secret: false, placeholder: '0' },
  ],
  generic: [
    {
      key: 'url',
      label: 'Source URL',
      secret: true,
      hint: 'The full report URL including its key, keeping the {from}/{to} placeholders.',
    },
  ],
};

/**
 * Replace credentials on THIS source. Every account is in the "has no token"
 * state after the legacy import, and the only way to fix one used to be Add
 * stats — which makes a second source (swaarm-loki-2) beside the first and
 * counts every feed twice. This posts to /api/accounts/:id/credentials: same
 * account, new secret, nothing else changes. It does not crawl; Run now does,
 * and a wrong token shows up as that crawl's error on the card. Edit's Source
 * URL does the same job from a pasted URL; this asks for the parts by name.
 */
function CredentialsForm({
  account: a,
  onClose,
}: {
  account: SettingsAccount;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const fields = CREDENTIAL_FIELDS[a.provider] ?? [];
  const [values, setValues] = useState<Partial<Record<CredKey, string>>>({});
  const [state, setState] = useState<{ tone: 'ok' | 'error'; text: string } | null>(null);
  const save = useMutation({
    mutationFn: (credentials: Partial<Record<CredKey, string>>) =>
      api.post<{ ok: boolean }>(`/api/accounts/${encodeURIComponent(a.id)}/credentials`, {
        credentials,
      }),
    onSuccess: () => {
      setValues({});
      setState({ tone: 'ok', text: 'Saved — Run now pulls with it.' });
      void qc.invalidateQueries({ queryKey: ['settings'] });
    },
    onError: (err: unknown) => setState({ tone: 'error', text: errorMessage(err) }),
  });
  // Every required field filled: the server refuses a blank anyway, but the
  // button saying so first is kinder than a round trip.
  const required = fields.filter((f) => f.key !== 'gid');
  const complete = required.every((f) => (values[f.key] ?? '').trim() !== '');

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setState(null);
    save.mutate(values);
  };

  return (
    <form onSubmit={submit} aria-label={`Replace credentials for ${a.label ?? a.id}`}>
      <div className="cfg-grid">
        {fields.map((f) => (
          <Field key={f.key} label={f.label} hint={f.hint} required={f.key !== 'gid'}>
            {(aria) => (
              <TextInput
                {...aria}
                type={f.secret ? 'password' : 'text'}
                reveal={f.secret}
                autoComplete="off"
                placeholder={f.placeholder}
                value={values[f.key] ?? ''}
                onChange={(e) => setValues((v) => ({ ...v, [f.key]: e.target.value }))}
              />
            )}
          </Field>
        ))}
      </div>
      <Toolbar className="cfg-actions">
        <Button type="submit" size="sm" loading={save.isPending} disabled={!complete}>
          Save credentials
        </Button>
        <Button variant="ghost" size="sm" onClick={onClose} disabled={save.isPending}>
          {state?.tone === 'ok' ? 'Done' : 'Cancel'}
        </Button>
        {state ? (
          <span
            className={state.tone === 'error' ? 'ui-field__error' : 'ui-saved ui-saved--ok'}
            role="status"
          >
            {state.text}
          </span>
        ) : null}
      </Toolbar>
    </form>
  );
}
